// src/admin/controllers/EventController.js
import moment from 'moment'
import { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import apiClient from '../../global/utils/apiClient'
import { useMetaStore } from '../../scoring/store/MetaStore'
import { useAdminStore } from '../store/AdminStore'
import EventView from '../views/EventView'

function EventController() {
  const [searchParams] = useSearchParams()
  const eventId = searchParams.get('eventId')
  const orgId = useAdminStore((state) => state.orgId)
  const orgName = useAdminStore((state) => state.orgName)
  const setGlobalModal = useMetaStore((state) => state.setGlobalModal)
  const [courses, setCourses] = useState([])
  const navigate = useNavigate()
  const [eventData, setEventData] = useState({
    orgId: orgId,
    courseId: '',
    eventDate: '',
    rainout: false,
    competition: false,
    countForHcp: false,
    holeCount: 9,
  })
  const [isCalculating, setIsCalculating] = useState(false)
  const [calculationProgress, setCalculationProgress] = useState(0)
  const pollingRef = useRef(false)

  useEffect(() => {
    // Fetch event data from the API when the component mounts
    if (eventId) {
      apiClient.get(`/gskapi/event/find/${eventId}`)
        .then((response) => {
          const data = response.data
          const formattedDate = moment(data.eventDate).format('YYYY-MM-DD')
          setEventData({
            countForHcp: false,
            ...data,
            eventDate: formattedDate,
          })
        })
        .catch((error) => {
          console.error('Error fetching event data:', error)
        })
    }
  }, [eventId])

  useEffect(() => {
    if (eventId && !eventData.finalized && !pollingRef.current) {
      pollStatus()
    }
  }, [eventId, eventData.finalized])

  const pollStatus = useCallback(async () => {
    if (!eventId) return
    pollingRef.current = true
    try {
      const res = await apiClient.get(`/gskapi/rounds/status/${eventId}`)
      const { status, progress } = res.data

      if (status === 'calculating') {
        setIsCalculating(true)
        setCalculationProgress(progress)
        setTimeout(pollStatus, 2000)
      } else if (status === 'complete') {
        setIsCalculating(false)
        setCalculationProgress(100)
        setEventData((prev) => ({ ...prev, finalized: true }))
        pollingRef.current = false
      } else {
        // idle or error
        setIsCalculating(false)
        pollingRef.current = false
      }
    } catch (err) {
      console.error('Error polling status:', err)
      setIsCalculating(false)
      pollingRef.current = false
    }
  }, [eventId])

  useEffect(() => {
    // Fetch the courses for the organization
    if (orgId) {
      apiClient.get(`/gskapi/courses/allcourses`)
        .then((response) => {
          setCourses(response.data)
        })
        .catch((error) => {
          console.error('Error fetching courses:', error)
        })
    }
  }, [orgId])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    if (name === 'holeCount') {
      setEventData({ ...eventData, [name]: checked ? 18 : 9 })
    } else if (type === 'checkbox') {
      setEventData({ ...eventData, [name]: checked })
    } else {
      setEventData({ ...eventData, [name]: value })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const response = await apiClient.post('/gskapi/event/save', eventData)

      if (response.status === 200 || response.status === 201) {
        console.log('Event data saved successfully!')
        setGlobalModal(true, 'Save Successful', '#060')
        const isArchive = eventData.eventDate && moment(eventData.eventDate).year() < moment().year()
        navigate(isArchive ? '/admin/events/archive' : '/admin/events')
      } else {
        console.error('Failed to save event data.')
        setGlobalModal(true, 'Failed to save event data.', '#900')
      }
    } catch (error) {
      console.error('Error saving event data:', error)
      setGlobalModal(true, 'Error saving event data.', '#900')
    }
  }

  const handleDelete = async () => {
    try {
      apiClient
        .post(`/gskapi/event/delete`, { eventId: eventId })
        .then((res) => {
          if (res.status === 200) {
            const isArchive = eventData.eventDate && moment(eventData.eventDate).year() < moment().year()
            navigate(isArchive ? '/admin/events/archive' : '/admin/events')
          } else {
            console.error(`Error deleting event: status ${res.status}`)
          }
        })
    } catch (error) {
      console.error('Error deleting event:', error)
    }
  }

  const handleFinalize = async () => {
    setIsCalculating(true)
    setCalculationProgress(0)
    try {
      apiClient
        .post(`/gskapi/rounds/finalize`, { eventId: eventId, orgId: orgId })
        .then((res) => {
          console.log('SUCCESS - event finalized')
          setGlobalModal(true, 'Event Finalization Started', '#060')
          pollStatus()
        })
        .catch(err => {
          console.error('Error finalizing event:', err)
          setIsCalculating(false)
          setGlobalModal(true, 'Error finalizing event.', '#900')
        })
    } catch (error) {
      console.error('Error finalizing event:', error)
      setIsCalculating(false)
      setGlobalModal(true, 'Error finalizing event.', '#900')
    }
  }

  const handleUnlock = async () => {
    try {
      apiClient
        .post(`/gskapi/event/unfinalize`, { eventId: eventId })
        .then((res) => {
          console.log('SUCCESS - event unlocked')
          setGlobalModal(true, 'Event Unlocked Successfully', '#060')
          setEventData((prev) => ({ ...prev, finalized: false }))
        })
    } catch (error) {
      console.error('Error unlocking event:', error)
      setGlobalModal(true, 'Error unlocking event.', '#900')
    }
  }

  const handleCancel = async () => {
    navigate(-1)
  }
  return (
    <EventView
      eventData={eventData}
      orgName={orgName}
      handleChange={handleChange}
      handleSubmit={handleSubmit}
      handleDelete={handleDelete}
      handleCancel={handleCancel}
      handleFinalize={handleFinalize}
      handleUnlock={handleUnlock}
      isCalculating={isCalculating}
      calculationProgress={calculationProgress}
      courses={courses}
    />
  )
}

export default EventController
